import { memo, useCallback } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Grid, Button, Switch, FormControlLabel } from '@mui/material';

import { InputField } from 'components';

import { SelectOptionRowProps } from './types';

export const SelectOptionRow = memo(
  ({
    idx,
    name,
    remove,
    isTablet,
    itemsLength,
    answerValues,
    correctOptions,
    setAnswerValuesValue,
    setCorrectOptionsValue,
    isCorrectOptionsEnabled,
    allowReferenceCodes = false,
  }: SelectOptionRowProps) => {
    const isCorrect = correctOptions.includes(idx);
    const handleToggleCorrect = useCallback(
      (checked: boolean) => {
        const nextValue = [...correctOptions];

        if (checked && !isCorrect) {
          nextValue.push(idx);
          setCorrectOptionsValue(nextValue, true);
          return;
        }

        if (!checked && isCorrect) {
          const index = nextValue.indexOf(idx);
          nextValue.splice(index, 1);
          setCorrectOptionsValue(nextValue, true);
        }
      },
      [correctOptions, idx, isCorrect, setCorrectOptionsValue]
    );

    const handleRemove = useCallback(() => {
      remove(idx);
      setAnswerValuesValue(
        answerValues.filter((_, index) => index !== idx),
        true
      );

      if (!isCorrectOptionsEnabled) {
        return;
      }

      const index = correctOptions.indexOf(idx);
      if (index === -1) {
        return;
      }

      const nextValue = [...correctOptions];
      nextValue.splice(index, 1);

      const adjustedValue = nextValue.map((correctIdx) =>
        correctIdx > idx ? correctIdx - 1 : correctIdx
      );
      setCorrectOptionsValue(adjustedValue, true);
    }, [
      correctOptions,
      answerValues,
      idx,
      isCorrectOptionsEnabled,
      remove,
      setAnswerValuesValue,
      setCorrectOptionsValue,
    ]);

    const actions = (
      <div className="flex h-14 gap-1">
        {isCorrectOptionsEnabled && (
          <FormControlLabel
            label="Correct"
            control={
              <Switch
                checked={isCorrect}
                name={`${name}.options.correctOptions.${idx}`}
                onChange={(event) => handleToggleCorrect(event.target.checked)}
              />
            }
          />
        )}
        {itemsLength > 1 && (
          <Button color="error" onClick={handleRemove}>
            <DeleteIcon />
          </Button>
        )}
      </div>
    );

    return (
      <Grid container spacing={2} className="items-center">
        <Grid size={{ md: 4, xs: 12 }}>
          <div className="flex gap-2">
            <InputField
              fullWidth
              label="Select option"
              placeholder="Select option"
              name={`${name}.options.options.${idx}`}
            />
            {!isTablet && actions}
          </div>
        </Grid>

        {allowReferenceCodes && (
          <Grid size={{ md: 4, xs: 12 }}>
            <InputField
              fullWidth
              label="Value"
              placeholder={String(idx + 1)}
              slotProps={{ inputLabel: { shrink: true } }}
              name={`${name}.options.answerValues.${idx}`}
            />
          </Grid>
        )}

        {isTablet && (
          <Grid size={{ xs: 12, md: allowReferenceCodes ? 4 : 8 }}>
            {actions}
          </Grid>
        )}
      </Grid>
    );
  }
);

SelectOptionRow.displayName = 'SelectOptionRow';
